import React, { useState } from 'react';
import { ChevronDown, ChevronLeft } from 'lucide-react';
import { Divider } from '@mui/material';
import { useSidebar } from '../../context/SidebarContext';
import link from '../../assets/link.svg';

interface ActivityItem {
  type: 'post' | 'comment' | 'job';
  title: string;
  date: string;
  description?: string;
  url?: string;
  companyName: string;
  signals?: Array<{
    intent_lead_id: string;
    intent_signal_id: string;
    signal: string;
    social_media_entities: Array<{
      type: string;
      url: string;
      title: string | null;
      description: string;
      created_at: string;
    }>;
    alignment: boolean;
    alignment_reasoning: string;
    alignment_score: string;
  }>;
}

interface ActivityGroup {
  month: string;
  items: ActivityItem[];
}

interface ActivityYear {
  year: string;
  groups: ActivityGroup[];
}

interface ActivityCardProps {
  activities: ActivityYear[];
  className?: string;
  expandedActivity: number;
  onActivityExpand: (index: number) => void;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }
  
  // Format the date using Intl.DateTimeFormat for better localization
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date);
};

const ActivityCard: React.FC<ActivityCardProps> = ({ activities, className = '', expandedActivity, onActivityExpand }) => {
  const { isSidebarOpen } = useSidebar();
  const [expandedMonths, setExpandedMonths] = useState<{ [key: string]: boolean }>(() => {
    const initialState: { [key: string]: boolean } = {};
    activities.forEach(yearGroup => {
      yearGroup.groups.forEach(monthGroup => {
        initialState[`${yearGroup.year}-${monthGroup.month}`] = true;
      });
    });
    return initialState;
  });

  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>(() => {
    const initialState: { [key: string]: boolean } = {};
    
    for (const yearGroup of activities) {
      for (const monthGroup of yearGroup.groups) {
        if (monthGroup.items.length > 0) {
          const firstItemKey = `${yearGroup.year}-${monthGroup.month}-0`;
          initialState[firstItemKey] = true;
          return initialState;
        }
      }
    }
    
    return initialState;
  });

  const toggleMonth = (monthKey: string) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  };

  const toggleItem = (itemKey: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  console.log(activities)

  const getActivityTitle = (item: ActivityItem) => {
    switch (item.type) {
      case 'job':
        return (
          <span>
            <span className="text-[#000000]">{item.companyName}</span>
            <span className="text-[#75777C]"> has announced a </span>
            <span className="text-[#000000]">job opening</span>
          </span>
        );
      case 'comment':
        return (
          <span>
            <span className="text-[#000000]">{item.companyName}</span>
            <span className="text-[#75777C]"> added </span>
            <span className="text-[#000000]">comment</span>
            <span className="text-[#75777C]"> on a post</span>
          </span>
        );
      case 'post':
        return (
          <span>
            <span className="text-[#000000]">{item.companyName}</span>
            <span className="text-[#75777C]"> recently shared a </span>
            <span className="text-[#000000]">new post</span>
          </span>
        );
      default:
        return item.title;
    }
  };

  return (
    <div className={`
      flex 
      flex-col 
      w-full 
      items-start 
      transition-all 
      duration-300
      ${className}
      font-inter
    `}>
      <div className="text-[20px] font-normal text-[#222222] mb-[18px]">
        Activity
      </div>
      <div className="flex flex-col w-full overflow-y-auto scrollbar-hide max-h-[calc(100vh-100px)]">
        {activities.map((yearGroup, yearIndex) => (
          <div key={yearIndex} className="flex flex-col">
            <div className="flex items-start gap-2 text-[14px] text-[#9FA1A7] text-left font-light mb-[18px]">
              {yearGroup.year}
            </div>
            
            <div className="flex flex-col">
              {yearGroup.groups.map((monthGroup, monthIndex) => (
                <div key={monthIndex} className="flex flex-col mb-4">
                  <button
                    onClick={() => toggleMonth(`${yearGroup.year}-${monthGroup.month}`)}
                    className="flex items-start justify-between gap-2 text-[12px] text-[#222222] font-light text-left hover:bg-gray-50 rounded-lg w-full"
                  >
                    <div className="flex items-center justify-between gap-2 w-full">
                      <div className="w-fit text-left border border-[#EEEFF1] px-2 py-1 rounded-[8px]">
                        {monthGroup.month}
                      </div>
                      <div className="flex-1">
                        <Divider className="h-[1px] w-full bg-[#F4F5F6]"/>
                      </div>
                      <div className="flex items-start justify-center w-4">
                        {expandedMonths[`${yearGroup.year}-${monthGroup.month}`] ? (
                          <ChevronDown className="w-4 h-4 text-[#7A7A7A]" />
                        ) : (
                          <ChevronLeft className="w-4 h-4 text-[#7A7A7A]" />
                        )}
                      </div>
                    </div>
                  </button>
                  
                  {expandedMonths[`${yearGroup.year}-${monthGroup.month}`] && (
                    <div className="flex flex-col mt-[36px] px-2">
                      <table className="w-full">
                        <tbody>
                          {monthGroup.items.map((item, itemIndex) => {
                            const itemKey = `${yearGroup.year}-${monthGroup.month}-${itemIndex}`;
                            return (
                              <React.Fragment key={itemIndex}>
                                <tr className="group align-top">
                                  <td className="py-2 align-top">
                                    <div className="w-2 h-full relative flex py-2 items-center justify-center">
                                      <div className={`
                                        absolute w-[1px] h-[calc(100%+16px)] bg-[#EEEEEE] top-0 z-0
                                        ${expandedItems[itemKey] && item.description ? 'h-[calc(100%+500px)]' : 'h-[calc(100%+16px)]'}
                                      `}></div>
                                      <div className="absolute w-[14px] h-[14px] rounded-full bg-[#266DF0] z-10"></div>
                                      <div className="absolute w-[6px] h-[6px] rounded-full bg-[#FFFFFF] z-20"></div>
                                    </div>
                                  </td>
                                  <td className="flex justify-between text-xs w-full text-[#000000] font-normal py-2 align-top text-left">
                                    <button 
                                      onClick={() => toggleItem(itemKey)}
                                      className="text-left hover:text-[#266DF0] transition-colors"
                                    >
                                      {getActivityTitle(item)}
                                    </button>
                                    <div className="text-[12px] text-[#75777C] font-normal text-right">
                                      {formatDate(item.signals?.[0]?.social_media_entities?.[0]?.created_at || item.date)}
                                    </div>
                                  </td>
                                </tr>
                                {expandedItems[itemKey] && item.description && (
                                  <tr>
                                    <td colSpan={2} className="pl-6 pb-4">
                                      <div className='flex flex-col gap-4'>
                                        {/* Intent Signal */}
                                        <div className="block w-full mb-[16px] mt-[16px] relative">
                                          {/* Intent Signal Label */}
                                          <div className="relative flex items-center gap-2 text-xs text-[#237A57] font-normal text-left bg-[#E6F4EA] w-fit px-2 py-1 rounded-[8px]">
                                            Intent found 🔍
                                          </div>

                                          {/* L-shaped connector line */}
                                          <div className="absolute left-4 top-[28px] z-10">
                                            <div className="w-[1px] h-[20px] bg-[#EEEEEE]"></div>
                                            <div className="absolute bottom-0 w-[12px] h-[1px] bg-[#EEEEEE]"></div>
                                          </div>

                                          {/* Signal Content */}
                                          <div className="text-xs text-[#222222] bg-[#FFFFFF] border border-[#EEEEEE] p-2 rounded-lg transition-colors text-left whitespace-pre-wrap font-normal ml-[32px] mt-2 shadow-[0px_8px_28px_-6px_#1C28401F] backdrop-blur-[10px]">
                                            {item.signals && item.signals[0]?.signal || 'No intent signal available'}
                                          </div>
                                        </div>

                                        {/* Research */}
                                        <div className="block w-full mb-[16px] relative">
                                          {/* Intent Found Label */}
                                          <div className="relative flex items-center gap-2 text-xs text-[#1565C0] font-normal text-left bg-[#E3F2FD] w-fit px-2 py-1 rounded-[8px]">
                                            Key findings ✍️
                                          </div>

                                          {/* L-shaped connector line */}
                                          <div className="absolute left-4 top-[28px] z-10">
                                            <div className="w-[1px] h-[20px] bg-[#EEEEEE]"></div>
                                            <div className="absolute bottom-0 w-[12px] h-[1px] bg-[#EEEEEE]"></div>
                                          </div>

                                          {/* Reasoning Content */}
                                          <div className="text-xs text-[#222222] bg-[#FFFFFF] border border-[#EEEEEE] p-2 rounded-lg transition-colors text-left whitespace-pre-wrap font-normal ml-[32px] mt-2 shadow-[0px_8px_28px_-6px_#1C28401F] backdrop-blur-[10px]">
                                            {item.signals && item.signals[0]?.alignment_reasoning 
                                              ? item.signals[0].alignment_reasoning
                                              : 'No insights available'}
                                          </div>
                                        </div>

                                        {/* Description */}
                                        <div className="block w-full mb-[16px] relative">
                                          {/* Description Label */}
                                          <div className="relative flex items-center gap-2 font-normal text-left bg-[#FFF3E0] w-fit px-2 py-1 rounded-[8px]">
                                            <span className="text-xs font-normal text-[#EF6C00]">
                                              {item.type === 'comment' ? 'Comment 💬: ' : 
                                              item.type === 'post' ? 'Post 📬: ' : 
                                              item.type === 'job' ? 'Job Description 📄: ' : ''}
                                            </span>
                                          </div>

                                          {/* L-shaped connector line */}
                                          <div className="absolute left-4 top-[28px] z-10">
                                            <div className="w-[1px] h-[20px] bg-[#EEEEEE]"></div>
                                            <div className="absolute bottom-0 w-[12px] h-[1px] bg-[#EEEEEE]"></div>
                                          </div>

                                          {/* Description Content */}
                                          <div className="flex flex-col items-start justify-center min-h-[40px] text-xs text-[#222222] bg-[#FAFAFA] border border-[#EEEEEE] p-2 rounded-lg transition-colors cursor-pointer text-left whitespace-pre-wrap font-semibold ml-[32px] mt-2">
                                            <div className="flex items-center justify-between">
                                              <div className="overflow-hidden text-ellipsis w-[calc(100%-20px)]">
                                                <div className="line-clamp-1">
                                                  {item.description || 'No description available'}
                                                </div>
                                              </div>
                                              <img src={link} alt={item.companyName} className="w-4 h-4" />
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivityCard; 